import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { clsx } from 'clsx';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  target: string; // CSS selector for the element to highlight
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

// const ONBOARDING_STEPS: OnboardingStep[] = [
//   {
//     id: 'welcome',
//     title: 'Welcome to Your Meeting!  \u{1F91D}',
//     description: 'Let\'s take a quick tour of the key features. This will only take 30 seconds.',
//     target: '',
//     position: 'center',
//   },
//   {
//     id: 'audio',
//     title: 'Audio Controls',
//     description: 'Click the microphone button to mute/unmute yourself. Your audio is off by default.',
//     target: '[data-tour="audio-button"]',
//     position: 'top',
//   },
//   {
//     id: 'screen-share',
//     title: 'Screen Sharing',
//     description: 'Share your screen with up to 4 participants. On mobile, swipe to navigate between shared screens.',
//     target: '[data-tour="screen-share-button"]',
//     position: 'top',
//   },
//   {
//     id: 'enlarge',
//     title: 'Enlarge Screens',
//     description: 'Hover over any shared screen and click the maximize button for a full-screen view.',
//     target: '[data-tour="screen-grid"]',
//     position: 'bottom',
//   },
//   {
//     id: 'participants',
//     title: 'Participant List',
//     description: 'See who\'s in the meeting and their connection status. Green rings indicate someone is speaking.',
//     target: '[data-tour="participants"]',
//     position: 'left',
//   },
//   {
//     id: 'complete',
//     title: 'You\'re All Set! ✨',
//     description: 'Start your meeting by unmuting your microphone and sharing your screen. Need help? Check the help menu.',
//     target: '',
//     position: 'center',
//   },
// ];

const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Your Meeting! 🤝',
    description: 'Let\'s take a quick tour of the key features. This will only take 30 seconds.',
    target: '',
    position: 'center',
  },
  {
    id: 'audio',
    title: 'Audio Controls',
    description: 'Click the microphone button to mute/unmute yourself. Your audio is off by default.',
    target: '[data-tour="audio-button"]',
    position: 'top',
  },
  {
    id: 'screen-share',
    title: 'Screen Sharing',
    description: 'Share your screen with up to 4 participants. On mobile, swipe to navigate between shared screens.',
    target: '[data-tour="screen-share-button"]',
    position: 'top',
  },
  {
    id: 'enlarge',
    title: 'Enlarge Screens',
    description: 'Hover over any shared screen and click the maximize button for a full-screen view.',
    target: '[data-tour="screen-grid"]',
    position: 'bottom',
  },
  {
    id: 'participants',
    title: 'Participant List',
    description: 'See who\'s in the meeting and their connection status. Green rings indicate someone is speaking.',
    target: '[data-tour="participants"]',
    position: 'left',
  },
  {
    id: 'complete',
    title: 'You\'re All Set! ✨',
    description: 'Start your meeting by unmuting your microphone and sharing your screen. Need help? Check the help menu.',
    target: '',
    position: 'center',
  },
];

const STORAGE_KEY = 'meeting-onboarding-completed';

interface MeetingOnboardingProps {
  onComplete?: () => void;
  autoStart?: boolean;
}

export const MeetingOnboarding: React.FC<MeetingOnboardingProps> = ({
  onComplete,
  autoStart = true,
}) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState<HTMLElement | null>(null);

  const step = ONBOARDING_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;

  // Check if user has completed onboarding
  useEffect(() => {
    const hasCompleted = localStorage.getItem(STORAGE_KEY);
    if (!hasCompleted && autoStart) {
      // Start after a short delay to let the UI render
      setTimeout(() => setIsActive(true), 1000);
    }
  }, [autoStart]);

  // Highlight target element
  useEffect(() => {
    if (!isActive || !step.target) {
      setHighlightedElement(null);
      return;
    }

    const element = document.querySelector(step.target) as HTMLElement;
    if (element) {
      setHighlightedElement(element);
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isActive, currentStep, step.target]);

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsActive(false);
    onComplete?.();
  };

  // Get position of tooltip relative to highlighted element
  const getTooltipPosition = () => {
    if (!highlightedElement || step.position === 'center') {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }

    const rect = highlightedElement.getBoundingClientRect();
    const tooltipWidth = 400;
    const tooltipHeight = 200;
    const gap = 20;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let position: React.CSSProperties = {};

    switch (step.position) {
      case 'top':
        position = {
          top: `${Math.max(gap, rect.top - tooltipHeight - gap)}px`,
          left: `${Math.min(Math.max(tooltipWidth / 2 + gap, rect.left + rect.width / 2), viewportWidth - tooltipWidth / 2 - gap)}px`,
          transform: 'translateX(-50%)',
        };
        break;
      case 'bottom':
        position = {
          top: `${Math.min(viewportHeight - tooltipHeight - gap, rect.bottom + gap)}px`,
          left: `${Math.min(Math.max(tooltipWidth / 2 + gap, rect.left + rect.width / 2), viewportWidth - tooltipWidth / 2 - gap)}px`,
          transform: 'translateX(-50%)',
        };
        break;
      case 'left':
        // If not enough space on left, show on right instead
        if (rect.left - tooltipWidth - gap < 0) {
          position = {
            top: `${rect.top + rect.height / 2}px`,
            left: `${rect.right + gap}px`,
            transform: 'translateY(-50%)',
          };
        } else {
          position = {
            top: `${rect.top + rect.height / 2}px`,
            left: `${rect.left - tooltipWidth - gap}px`,
            transform: 'translateY(-50%)',
          };
        }
        break;
      case 'right':
        // If not enough space on right, show on left instead
        if (rect.right + tooltipWidth + gap > viewportWidth) {
          position = {
            top: `${rect.top + rect.height / 2}px`,
            left: `${rect.left - tooltipWidth - gap}px`,
            transform: 'translateY(-50%)',
          };
        } else {
          position = {
            top: `${rect.top + rect.height / 2}px`,
            left: `${rect.right + gap}px`,
            transform: 'translateY(-50%)',
          };
        }
        break;
    }

    return position;
  };

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Highlight spotlight */}
      {highlightedElement && (
        <div
          className="absolute pointer-events-none"
          style={{
            top: highlightedElement.getBoundingClientRect().top - 8,
            left: highlightedElement.getBoundingClientRect().left - 8,
            width: highlightedElement.getBoundingClientRect().width + 16,
            height: highlightedElement.getBoundingClientRect().height + 16,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
            borderRadius: '12px',
            border: '3px solid #3b82f6',
            animation: 'pulse 2s ease-in-out infinite',
          }}
        />
      )}

      {/* Tooltip */}
      // In MeetingOnboarding component, update tooltip className:
<div
  className="absolute bg-neutral-900 border border-white/20 rounded-xl shadow-2xl p-6 max-w-md"
  style={getTooltipPosition()}
>
  {/* Close button */}
  <button
    onClick={handleSkip}
    className="absolute top-4 right-4 text-neutral-400 hover:text-white transition-colors"
  >
    <X size={20} />
  </button>

  {/* Progress indicator */}
  <div className="flex gap-1 mb-4">
    {ONBOARDING_STEPS.map((_, index) => (
      <div
        key={index}
        className={clsx(
          'h-1 flex-1 rounded-full transition-colors',
          index <= currentStep ? 'bg-primary-500' : 'bg-neutral-700'
        )}
      />
    ))}
  </div>

  {/* Content */}
  <div className="mb-6">
    <h3 className="text-xl font-bold text-white mb-2">
      {step.title}
    </h3>
    <p className="text-neutral-300 leading-relaxed">
      {step.description}
    </p>
  </div>

  {/* Navigation */}
  <div className="flex items-center justify-between">
    <button
      onClick={handleSkip}
      className="text-sm text-neutral-400 hover:text-white transition-colors"
    >
      Skip tour
    </button>

    <div className="flex gap-2">
      {!isFirstStep && (
        <button
          onClick={handlePrev}
          className="px-4 py-2 border border-white/20 hover:bg-white/10 rounded-lg transition-all flex items-center gap-2 text-white"
        >
          <ChevronLeft size={16} />
          Back
        </button>
      )}
      <button
        onClick={handleNext}
        className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-all flex items-center gap-2"
      >
        {isLastStep ? (
          <>
            Complete
            <Check size={16} />
          </>
        ) : (
          <>
            Next
            <ChevronRight size={16} />
          </>
        )}
      </button>
    </div>
  </div>

  {/* Step counter */}
  <div className="text-center text-sm text-neutral-500 mt-4">
    {currentStep + 1} of {ONBOARDING_STEPS.length}
  </div>
</div>
    </div>
  );
};

// Hook to manually trigger onboarding
export const useOnboarding = () => {
  const [show, setShow] = useState(false);

  const start = () => {
    localStorage.removeItem(STORAGE_KEY);
    setShow(true);
  };

  const reset = () => {
    localStorage.removeItem(STORAGE_KEY);
  };

  return { show, start, reset };
};